import { FC, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import {
  Box,
  Checkbox,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";

import { useTemplateMeasureRows } from "../../hooks/use-template-measure-rows";
import { MULTI_ANALYSIS_MEASURE_TYPE, type IMultiAnalysisTimeRange, type IStructureTreePoint } from "../../types";
import type { IMultiAnalysisTemplateCell } from "../../utils/analysis-template-settings";
import {
  buildFiltersFromMeasureRows,
  buildMeasureRowLabel,
  getSelectedMeasureRowKeys,
} from "../../utils/template-measure-rows";

const MEASURE_TYPE_OPTIONS = [
  MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM,
  MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM,
  MULTI_ANALYSIS_MEASURE_TYPE.ORBIT,
  MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND,
] as const;

interface IMeasureListTemplateCellEditorProps {
  cell: IMultiAnalysisTemplateCell;
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisTimeRange;
  onChange: (patch: Partial<Pick<IMultiAnalysisTemplateCell, "measureType" | "filters">>) => void;
}

export const MeasureListTemplateCellEditor: FC<IMeasureListTemplateCellEditorProps> = ({
  cell,
  points,
  range,
  onChange,
}) => {
  const { rows, isLoading } = useTemplateMeasureRows({
    enabled: points.length > 0,
    measureType: cell.measureType,
    points,
    startTimestamp: String(range.start ?? ""),
    endTimestamp: String(range.end ?? ""),
  });
  const selectedRowKeys = useMemo(() => getSelectedMeasureRowKeys(rows, cell.filters), [cell.filters, rows]);

  const handleToggleRow = (rowKey: string, checked: boolean) => {
    const nextKeys = checked
      ? Array.from(new Set([...selectedRowKeys, rowKey]))
      : selectedRowKeys.filter(key => key !== rowKey);

    onChange({
      filters: buildFiltersFromMeasureRows(rows, nextKeys),
    });
  };

  return (
    <Stack spacing={1}>
      <TextField
        select
        size="small"
        label={<FormattedMessage id="pages.multiAnalysis.templateEditor.displayType" />}
        value={cell.measureType}
        onChange={event => {
          onChange({
            measureType: event.target.value as MULTI_ANALYSIS_MEASURE_TYPE,
            filters: {},
          });
        }}
      >
        {MEASURE_TYPE_OPTIONS.map(option => (
          <MenuItem key={option} value={option}>
            <FormattedMessage id={`pages.multiAnalysis.measureWindow.${option}`} />
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ border: "1px solid #d8ddea", borderRadius: 1, overflow: "hidden" }}>
        <ProgressOverlay isLoading={isLoading} wrapperSx={{ minHeight: 120 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>
                  <FormattedMessage id="pages.multiAnalysis.templateEditor.measureName" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.map(row => (
                  <TableRow key={row.key} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedRowKeys.includes(row.key)}
                        onChange={event => handleToggleRow(row.key, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12 }}>{buildMeasureRowLabel(row)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography sx={{ fontSize: 12, color: "#8b93a7", py: 1.5, textAlign: "center" }}>
                      <FormattedMessage id="pages.multiAnalysis.templateEditor.noMeasures" />
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ProgressOverlay>
      </Box>
    </Stack>
  );
};
